import { DateSchema, FALL_RISK, TIME_FRAME } from "@packages/core";
import { Link, createFileRoute, useRouter } from "@tanstack/react-router";
import { useMemo } from "react";
import { LuArrowLeft, LuArrowRight, LuChevronLeft } from "react-icons/lu";
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { Temporal } from "temporal-polyfill";
import * as v from "valibot";

import {
  Breadcrumbs,
  ButtonStyle,
  PageName,
  PaperStyle,
  TypographyStyle,
  useResponsiveLineChartStyles,
} from "#components";
import { useFallRiskEntryInfiniteQuery, useResident, useTranslate } from "#services";
import { css } from "#styled-system/css";

export const Route = createFileRoute(
  "/_authenticated/facilities/$facilityKey/residents/$userKey/fall-risks",
)({
  component: Component,
  validateSearch: v.object({
    date: v.fallback(v.optional(DateSchema()), undefined),
    numberOfDays: v.fallback(v.optional(v.pipe(v.number(), v.integer(), v.minValue(7))), undefined),
  }),
});

function Component() {
  const { facilityKey, userKey } = Route.useParams();
  const { date: _date, numberOfDays = 7 } = Route.useSearch();

  const resident = useResident(userKey);
  const router = useRouter();
  const translate = useTranslate();

  const date = useMemo(
    function () {
      return _date ?? Temporal.Now.plainDateISO("Asia/Tokyo");
    },
    [_date],
  );

  const fallRiskEntryInfiniteQuery = useFallRiskEntryInfiniteQuery({
    condition: [
      ["userKey", "=", userKey],
      [
        "assessedAt",
        "BETWEEN",
        date.subtract({ days: numberOfDays }).toPlainDateTime({ hour: TIME_FRAME.DAYTIME[0] }),
        "AND",
        date.add({ days: 1 }).toPlainDateTime({ hour: TIME_FRAME.DAYTIME[0] }),
      ],
    ],
  });

  const fallRiskEntries = useMemo(
    function () {
      return (
        fallRiskEntryInfiniteQuery.data?.pages.flatMap(function (page) {
          return page.fallRiskEntries;
        }) ?? []
      );
    },
    [fallRiskEntryInfiniteQuery.data],
  );

  const data = useMemo(
    function () {
      return fallRiskEntries.map(function (fallRisk) {
        return {
          fallRisk,
          x: fallRisk.assessedAt.toZonedDateTime("Asia/Tokyo").epochMilliseconds,
          y: fallRisk.fallRisk,
        };
      });
    },
    [fallRiskEntries],
  );

  const ticks = useMemo(
    function () {
      const start = date.subtract({ days: numberOfDays - 1 });
      const end = date.add({ days: 1 });
      return Array.from({ length: start.until(end).days + 1 }, function (_, i) {
        return start.add({ days: i }).toZonedDateTime("Asia/Tokyo").epochMilliseconds;
      }).filter(function (_, index, { length }) {
        return numberOfDays > 7
          ? index === 0 || index === length - 1 || index % Math.round(length / 4) === 0
          : true;
      });
    },
    [date, numberOfDays],
  );

  const styles = useResponsiveLineChartStyles();

  return (
    <main
      className={css({
        paddingLeft: { smDown: "0 !important" },
        paddingRight: { smDown: "0 !important" },
      })}
    >
      <PageName>
        {translate("%FallRisk.0%") + " - " + (resident?.name ?? translate("%Loading.0%"))}
      </PageName>

      <div
        className={css({
          alignItems: "center",
          display: "flex",
          gap: "16px",
          justifyContent: "space-between",
        })}
      >
        <Link
          className={css({ alignItems: "center", display: "flex", gap: "4px", hideFrom: "sm" })}
          onClick={function (event) {
            if (router.history.canGoBack()) {
              event.preventDefault();
              event.stopPropagation();
              router.history.back();
            }
          }}
          params={{ facilityKey, userKey }}
          to="/facilities/$facilityKey/residents/$userKey"
        >
          <LuChevronLeft className={css({ height: "32px", width: "auto" })} />
          <p
            className={TypographyStyle({
              color: "primary",
              fontSize: "16px",
              fontWeight: 500,
              lineHeight: 1,
            })}
          >
            {translate("%HonorificTitle.0%", {
              $: resident ?? { name: translate("%Resident.0%") },
            })}
          </p>
        </Link>

        <Breadcrumbs
          className={css(
            TypographyStyle.raw({ fontSize: "14px", fontWeight: 500, lineHeight: 1 }),
            { display: "flex", hideBelow: "sm" },
          )}
        >
          <Link
            className={css({ color: "#71717A" })}
            params={{ facilityKey }}
            to="/facilities/$facilityKey"
          >
            {translate("%ResidentList.0%")}
          </Link>
          <Link
            className={css({ color: "#71717A" })}
            params={{ facilityKey, userKey }}
            to="/facilities/$facilityKey/residents/$userKey"
          >
            {translate("%HonorificTitle.0%", {
              $: resident ?? { name: translate("%Resident.0%") },
            })}
          </Link>
          <p>{translate("%FallRisk.0%")}</p>
        </Breadcrumbs>
      </div>

      <div
        className={css({
          backgroundColor: "white",
          borderRadius: { sm: "12px" },
          marginTop: "16px",
          padding: "16px",
        })}
      >
        <h3
          className={css(TypographyStyle.raw({ lineHeight: 1 }), {
            fontSize: { base: "16px", sm: "20px" },
            fontWeight: { base: 500, sm: 700 },
            textAlign: { base: "center", sm: "left" },
          })}
        >
          {translate("%FallRisk.0%")}
        </h3>

        <div
          className={css({
            backgroundColor: "muted-background",
            borderRadius: "6px",
            display: "flex",
            marginTop: "12px",
            padding: "4px",
            width: "100%",
          })}
        >
          {[7, 30].map(function (option, index) {
            return (
              <Link
                className={css(
                  TypographyStyle.raw({
                    fontSize: "14px",
                    fontWeight: 500,
                    lineHeight: 1,
                    textAlign: "center",
                  }),
                  {
                    borderRadius: "2px",
                    display: "block",
                    flexGrow: 1,
                    paddingX: "12px",
                    paddingY: "6px",
                    _selected: { backgroundColor: "background" },
                  },
                )}
                data-selected={numberOfDays === option || undefined}
                key={index}
                params={{ facilityKey, userKey }}
                replace
                search={{ date, numberOfDays: option }}
                to="/facilities/$facilityKey/residents/$userKey/fall-risks"
              >
                {option === 7
                  ? translate("%Week.0%")
                  : option === 30
                    ? translate("%Month.0%")
                    : translate("%NumberOfDays.0%", { $: { numberOfDays: option } })}
              </Link>
            );
          })}
        </div>

        <p
          className={css(TypographyStyle.raw({ color: "primary", fontSize: "14px" }), {
            marginTop: "12px",
            opacity: 0.6,
          })}
        >
          {translate("%Date.1%", { $: date.subtract({ days: numberOfDays - 1 }) })}〜
          {translate("%Date.1%", { $: date })}
        </p>

        {fallRiskEntries.length === 0 && (
          <p className={TypographyStyle({ color: "primary", fontSize: "36px", fontWeight: 700 })}>
            {translate("%NoData.1%")}
          </p>
        )}

        <div
          className={css({
            alignItems: "center",
            display: "flex",
            gap: "16px",
            justifyContent: "center",
          })}
        >
          <Link
            className={css(
              ButtonStyle.raw({
                appearance: "outlined",
                borderRadius: "100%",
                color: "outlined",
                icon: true,
                size: "32px",
              }),
              { hideBelow: "sm" },
            )}
            search={{ date: date.subtract({ days: numberOfDays }), numberOfDays }}
            to="."
          >
            <LuArrowLeft className={css({ height: "16px", width: "auto" })} />
          </Link>

          <ResponsiveContainer height={440} width="100%">
            <LineChart data={data}>
              <XAxis
                allowDataOverflow
                axisLine={false}
                dataKey="x"
                tick={{ fill: "#18181B4D", fontSize: "12px" }}
                tickFormatter={function (epoch) {
                  return numberOfDays > 7
                    ? translate("%Day.1%", {
                        $: Temporal.Instant.fromEpochMilliseconds(epoch).toZonedDateTimeISO(
                          "Asia/Tokyo",
                        ),
                      })
                    : translate(
                        "%DayOfWeek." +
                          Temporal.Instant.fromEpochMilliseconds(epoch).toZonedDateTimeISO(
                            "Asia/Tokyo",
                          ).dayOfWeek +
                          ".0%",
                      );
                }}
                tickLine={false}
                ticks={ticks}
                type="number"
              />
              <YAxis
                axisLine={false}
                dataKey="y"
                domain={[FALL_RISK.MILD, FALL_RISK.SEVERE]}
                padding={{ top: 40, bottom: 40 }}
                tick={{ fill: "#18181B4D", fontSize: "12px" }}
                tickFormatter={function (fallRisk) {
                  return translate("%FallRisk." + fallRisk + ".0%");
                }}
                tickLine={false}
                ticks={[FALL_RISK.MILD, FALL_RISK.MODERATE, FALL_RISK.SEVERE]}
                type="number"
                width={28}
              />
              <Tooltip
                content={function ({ payload }) {
                  const fallRisk: (typeof data)[number]["fallRisk"] =
                    payload.at(0)?.payload.fallRisk;
                  if (!fallRisk) {
                    return null;
                  }
                  return (
                    <div
                      className={css(PaperStyle.raw({ borderRadius: "12px" }), {
                        backgroundColor: "#FFFFFFCC",
                        padding: "16px",
                      })}
                    >
                      <p className={TypographyStyle({ color: "primary/60", fontSize: "12px" })}>
                        {translate("%DateTime.1%", { $: fallRisk.assessedAt })}
                      </p>
                      <p>
                        <span className={TypographyStyle({ color: "primary", fontSize: "12px" })}>
                          {translate("%FallRisk.0%")}
                        </span>
                        &nbsp;
                        <span
                          className={TypographyStyle({
                            color: "primary",
                            fontSize: "14px",
                            fontWeight: 700,
                          })}
                        >
                          {translate("%FallRisk." + fallRisk.fallRisk + ".0%")}
                        </span>
                      </p>
                    </div>
                  );
                }}
              />
              <CartesianGrid stroke="#E4E4E7" syncWithTicks vertical={false} />
              <Line activeDot={styles.lineDot} dataKey="y" dot={styles.lineDot} stroke="#DB2777" />
            </LineChart>
          </ResponsiveContainer>

          <Link
            className={css(
              ButtonStyle.raw({
                appearance: "outlined",
                borderRadius: "100%",
                color: "outlined",
                icon: true,
                size: "32px",
              }),
              { hideBelow: "sm" },
            )}
            search={{ date: date.add({ days: numberOfDays }), numberOfDays }}
            to="."
          >
            <LuArrowRight className={css({ height: "16px", width: "auto" })} />
          </Link>
        </div>
      </div>
    </main>
  );
}
